import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { User as FirebaseUser } from 'firebase/auth';
import { db } from './firebase';

export interface UserFlags {
  isReviewer: boolean;
  isAdmin: boolean;
  isOwner: boolean;
}

function mapUserFlags(data: Record<string, unknown> | undefined): UserFlags {
  return {
    isReviewer: data?.isReviewer === true,
    isAdmin: data?.isAdmin === true,
    isOwner: data?.isOwner === true,
  };
}

export async function ensureUserDocument(user: FirebaseUser): Promise<void> {
  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) {
    await setDoc(userRef, {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    });
    return;
  }

  await setDoc(userRef, {
    email: user.email,
    displayName: user.displayName,
    lastLoginAt: serverTimestamp(),
  }, { merge: true });
}

export async function getUserIsAdmin(uid: string): Promise<boolean> {
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return false;
  return snap.data().isAdmin === true;
}

export async function getUserIsReviewer(uid: string): Promise<boolean> {
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return false;
  return snap.data().isReviewer === true;
}

export async function getUserIsOwner(uid: string): Promise<boolean> {
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return false;
  return snap.data().isOwner === true;
}

export function subscribeToUserFlags(
  uid: string,
  onChange: (flags: UserFlags) => void,
  onError?: (error: Error) => void
): () => void {
  return onSnapshot(
    doc(db, 'users', uid),
    (snapshot) => {
      const flags = snapshot.exists()
        ? mapUserFlags(snapshot.data())
        : { isReviewer: false, isAdmin: false, isOwner: false };
      onChange(flags);
    },
    (error) => {
      console.error('[userFlags] Failed to subscribe to user flags:', {
        uid,
        error,
      });
      onError?.(error);
    }
  );
}

export async function getUserLabel(userId: string): Promise<string> {
  const snap = await getDoc(doc(db, 'users', userId));
  if (!snap.exists()) return 'Unknown';
  const data = snap.data();
  return data.displayName || data.email || 'Unknown';
}
